import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Card, QRCode, Select, Space, Spin, Switch, Typography } from 'antd';
import { LinkOutlined, DisconnectOutlined, SendOutlined } from '@ant-design/icons';
import {
  atualizarPreferenciasTelegram,
  desvincularTelegram,
  gerarLinkTelegram,
  getTelegramVinculo,
} from '../../services/mensagensService';
import { getContas } from '../../services/accountService';
import { getErrorMessage } from '../../utils/errorHandler';

const { Text, Paragraph } = Typography;

/**
 * Card de vínculo Telegram para Configurações.
 */
export default function TelegramVinculoCard({ isMobile }) {
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [vinculo, setVinculo] = useState(null);
  const [contas, setContas] = useState([]);
  const [pendingLink, setPendingLink] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [v, c] = await Promise.all([
        getTelegramVinculo(),
        getContas({ status: 'ATIVO' }).catch(() => []),
      ]);
      setVinculo(v);
      setContas(Array.isArray(c) ? c : c?.results || []);
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível carregar o vínculo do Telegram.'));
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVincular = async (force = false) => {
    try {
      setBusy(true);
      const data = await gerarLinkTelegram({ force });
      setPendingLink(data);
      if (data.deep_link) {
        window.open(data.deep_link, '_blank', 'noopener,noreferrer');
      }
      message.success('Link gerado. Abra o Telegram e confirme o vínculo (válido por 10 minutos).');
    } catch (err) {
      if (err?.response?.status === 409) {
        modal.confirm({
          title: 'Telegram já vinculado',
          content: 'Deseja gerar um novo link? Ao abrir no Telegram, o vínculo anterior deste canal será substituído.',
          okText: 'Gerar novo link',
          cancelText: 'Cancelar',
          onOk: () => handleVincular(true),
        });
        return;
      }
      message.error(getErrorMessage(err, 'Não foi possível gerar o link do Telegram.'));
    } finally {
      setBusy(false);
    }
  };

  const handleDesvincular = () => {
    modal.confirm({
      title: 'Desvincular Telegram?',
      content: 'Você deixará de poder lançar pelo bot até vincular novamente.',
      okText: 'Desvincular',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setBusy(true);
          await desvincularTelegram();
          setPendingLink(null);
          message.success('Telegram desvinculado.');
          await load();
        } catch (err) {
          message.error(getErrorMessage(err, 'Falha ao desvincular.'));
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleContaChange = async (contaId) => {
    try {
      setBusy(true);
      const v = await atualizarPreferenciasTelegram({ conta_padrao_id: contaId ?? null });
      setVinculo(v);
      message.success('Conta padrão atualizada.');
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível atualizar a conta padrão.'));
    } finally {
      setBusy(false);
    }
  };

  const handleStatusChange = async (statusPadrao) => {
    try {
      setBusy(true);
      const v = await atualizarPreferenciasTelegram({ status_padrao: statusPadrao });
      setVinculo(v);
      message.success('Status padrão atualizado.');
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível atualizar o status.'));
    } finally {
      setBusy(false);
    }
  };

  const handleAvisosChange = async (checked) => {
    try {
      setBusy(true);
      const v = await atualizarPreferenciasTelegram({ receber_avisos: checked });
      setVinculo(v);
      message.success(checked ? 'Avisos no Telegram ativados.' : 'Avisos no Telegram desativados.');
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível atualizar os avisos.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Lançamentos por Telegram"
      bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
      style={{ marginTop: 24 }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Lance despesas e receitas enviando texto ou foto de comprovante para o bot do Saldo Ativo.
            O vínculo é seguro: só funciona depois que você abrir o link gerado estando logado neste app.
            Nunca envie e-mail ou senha no Telegram. Áudio ainda não está disponível.
          </Paragraph>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="O que você pode fazer no bot"
            description={
              <div>
                <ul style={{ margin: '8px 0', paddingLeft: 18 }}>
                  <li>Lançar gastos e receitas em texto ou com foto de comprovante</li>
                  <li>Consultar saldo, o que vence hoje, faturas e metas (teclado do bot)</li>
                  <li>Receber avisos e marcar contas como pagas pelos botões</li>
                  <li>Pedir resumo da semana (arquivo CSV) com /resumo</li>
                </ul>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Exemplos (copie e cole no Telegram):
                </Text>
                {[
                  'gastei 45,90 no ifood',
                  'paguei 89 mercado ontem no cartão Nubank',
                  'qual meu saldo?',
                ].map((ex) => (
                  <div key={ex} style={{ marginBottom: 4 }}>
                    <Text code copyable>
                      {ex}
                    </Text>
                  </div>
                ))}
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                  No bot: toque em Ajuda ou digite /ajuda para o guia completo com exemplos clicáveis.
                </Paragraph>
              </div>
            }
          />

          {vinculo?.vinculado ? (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Telegram vinculado"
              description={
                <>
                  {vinculo.vinculado_em
                    ? `Ativo desde ${new Date(vinculo.vinculado_em).toLocaleString('pt-BR')}.`
                    : 'Sua conta está conectada ao bot.'}
                  {vinculo.workspace_titular_nome && (
                    <div style={{ marginTop: 4 }}>
                      Os lançamentos deste chat vão para o workspace de{' '}
                      <Text strong>{vinculo.workspace_titular_nome}</Text> (acesso compartilhado).
                    </div>
                  )}
                </>
              }
            />
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Telegram não vinculado"
              description="Clique em Vincular, abra o Telegram e confirme. O link expira em 10 minutos."
            />
          )}

          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={busy}
                onClick={() => handleVincular(false)}
              >
                {vinculo?.vinculado ? 'Gerar novo link (relink)' : 'Vincular Telegram'}
              </Button>
              {vinculo?.vinculado && (
                <Button danger icon={<DisconnectOutlined />} loading={busy} onClick={handleDesvincular}>
                  Desvincular
                </Button>
              )}
              {pendingLink?.deep_link && (
                <Button
                  icon={<LinkOutlined />}
                  href={pendingLink.deep_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Abrir link novamente
                </Button>
              )}
            </Space>

            {pendingLink?.deep_link && !vinculo?.vinculado && (
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: 'center' }}>
                <QRCode value={pendingLink.deep_link} size={128} />
                <Text type="secondary" style={{ wordBreak: 'break-all', fontSize: 12 }}>
                  {pendingLink.deep_link}
                </Text>
              </div>
            )}

            {vinculo?.vinculado && (
              <>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Conta padrão dos lançamentos
                  </Text>
                  <Select
                    style={{ width: '100%', maxWidth: 360 }}
                    placeholder="Selecione uma conta"
                    allowClear
                    value={vinculo.conta_padrao_id ?? undefined}
                    onChange={handleContaChange}
                    options={contas.map((c) => ({ value: c.id, label: c.nome }))}
                    disabled={busy}
                  />
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Status ao confirmar
                  </Text>
                  <Select
                    style={{ width: '100%', maxWidth: 360 }}
                    value={vinculo.status_padrao || 'PAGO'}
                    onChange={handleStatusChange}
                    options={[
                      { value: 'PAGO', label: 'Pago (já baixado)' },
                      { value: 'PENDENTE', label: 'Pendente' },
                    ]}
                    disabled={busy}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 360 }}>
                  <div>
                    <Text strong style={{ display: 'block' }}>
                      Receber avisos no Telegram
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Vencimentos, metas e outras notificações do app
                    </Text>
                  </div>
                  <Switch
                    checked={vinculo.receber_avisos !== false}
                    onChange={handleAvisosChange}
                    disabled={busy}
                  />
                </div>
              </>
            )}
          </Space>
        </>
      )}
    </Card>
  );
}
